/**
 * Safe Analytics Wrapper — PostHog
 *
 * Non-blocking, production-only event tracking via PostHog.
 * PostHog SDK handles its own batching and retry.
 *
 * Features:
 * - Non-blocking event tracking
 * - Automatic error handling
 * - Production-only tracking (or debug mode)
 * - Type-safe event properties
 * - Session recording, heatmaps, dead click detection
 */

import type { AnalyticsEventName } from '@bower/shared/constants/tracking-plan.generated';
import posthog from 'posthog-js';
import { APP_ENV, IS_PROD, POSTHOG_KEY, POSTHOG_HOST, ANALYTICS_DEBUG } from '../config/env';
import { isProductionHost } from '../config/productionHost';
import type { BaseEventProperties, UserProperties, PageViewType } from '../types/analytics';
import { PageCategory } from '../types/analytics';
import { ANALYTICS_EVENTS } from '../constants/analytics';
import { hasAnalyticsConsent } from './cookieConsent';
import { subscribeRegionConsentDefault } from './regionConsentDefault';
import { revokeGa4Consent } from './ga4';
import {
  isBrowserExtensionError,
  isCrossOriginScriptError,
  isResizeObserverLoopError,
} from './errorFilters';
import {
  dropLinkTargets,
  dropRetiredSuperProperties,
  forgetRetiredFlagProperties,
  redactUrlCredentials,
  redactEventCredentials,
  stripExceptionMessages,
  stripUrlQuery,
} from './analyticsRedaction';

class SafeAnalytics {
  private isInitialized = false;
  private isProduction = IS_PROD;
  private isDebug = ANALYTICS_DEBUG;
  /** Feature-flags-only mode: PostHog initialised for flags + identify, but no event capture */
  private isFeatureFlagsOnly = false;
  /**
   * PHI status of the ACTIVE workspace. `null` = not yet resolved.
   *
   * Session replay ($snapshot) records the rendered DOM and bypasses
   * `before_send` entirely, so note bodies, artifact titles and protocol text
   * reach PostHog verbatim. `config/egress-policy.yaml` declares POSTHOG
   * `baa_covered: false`, so that content must never leave a PHI workspace —
   * the same rule that already hides Connectors from PHI workspaces
   * (`useIsPhiWorkspace`). The server-side boundary (`mayEgressContent`) cannot
   * see this path because replay never round-trips through the backend.
   */
  private isPhiWorkspace: boolean | null = null;
  /**
   * Sticky for the life of the page: has this browsing session ever been
   * inside a PHI workspace? Once true, content capture stays off until a full
   * reload — logging out sets `currentUserId` to null, which would otherwise
   * hit the signed-out allowance and restart the recorder over PHI DOM that
   * has not necessarily unmounted yet.
   */
  private phiSessionTainted = false;
  private currentUserId: string | null = null;
  /** Auth may hydrate before deferred PostHog initialization; retain identity for the first /flags evaluation. */
  private currentUserProperties: Partial<UserProperties> | null = null;
  private preLogoutCallbacks: Set<() => void> = new Set();

  /** Whether analytics event capture is active (production OR debug mode, NOT feature-flags-only) */
  private get isActive(): boolean {
    return this.isInitialized && (this.isProduction || this.isDebug) && !this.isFeatureFlagsOnly;
  }

  /** Whether PostHog is initialised (includes feature-flags-only mode) */
  private get isPostHogReady(): boolean {
    return this.isInitialized && !!POSTHOG_KEY;
  }

  /**
   * Whether any DOM-content capture may run right now. FAILS CLOSED.
   *
   * Governs every PostHog option that can carry page content to a
   * `baa_covered: false` sink — not just session replay. `autocapture` attaches
   * `$el_text` (up to 1024 chars of element text) for `a`/`button`/`form`/
   * `input`/`select`/`textarea`/`label` unless `mask_all_text` is set. Bower
   * sets it (and `mask_all_element_attributes`) for every workspace, and masks
   * all replay text; this gate is the second layer, turning the whole family
   * off where content must not leave at all. Heatmaps, dead clicks and
   * exception capture ride along for the same reason.
   *
   * Four gates, in order:
   *  1. Consent — flags-only mode never captures (GDPR Art. 7).
   *  2. PHI taint — this browsing session has been inside a PHI workspace at
   *     some point, so nothing resumes until a full page load. See below.
   *  3. PHI — a workspace with `settings.phi === true` never captures.
   *  4. Unresolved PHI for a SIGNED-IN user — `null` means the workspace
   *     hasn't loaded yet, and `useIsPhiWorkspace` returns `false` until it
   *     does, so trusting it here would capture the first seconds of every PHI
   *     session. Blocked until proven non-PHI.
   *
   * Signed-OUT (`currentUserId === null`) is deliberately allowed: the
   * marketing, `/auth` and public-share surfaces carry no workspace content,
   * and blocking them would drop the signup-funnel replays without buying any
   * PHI protection. The taint check must sit AHEAD of that allowance — logging
   * out of a PHI workspace clears `currentUserId` and would otherwise land on
   * the signed-out branch and re-open capture while PHI DOM may still be
   * painted (rrweb restarts with a FULL-document snapshot).
   */
  private get isContentCaptureAllowed(): boolean {
    if (this.isFeatureFlagsOnly) return false;
    if (this.phiSessionTainted) return false;
    if (this.isPhiWorkspace === true) return false;
    if (this.isPhiWorkspace === null && this.currentUserId !== null) return false;
    return true;
  }

  /**
   * Initialize PostHog with error handling.
   *
   * Consent-aware:
   * - If analytics consent is given → full PostHog (events, recordings, heatmaps)
   * - If consent denied or not yet given → feature-flags-only mode (no tracking)
   * - In dev without ANALYTICS_DEBUG → feature-flags-only regardless of consent
   *
   * Call `enableAnalyticsAfterConsent()` when the user grants analytics consent
   * after initial page load to upgrade from flags-only to full tracking.
   *
   * @compliance GDPR Article 7 — no analytics before explicit consent
   */
  init(): void {
    // In dev without debug mode, always use feature-flags-only
    const isDevNoDebug = !this.isProduction && !this.isDebug;
    // In production, check consent before enabling full analytics
    const consentGranted = this.isProduction && hasAnalyticsConsent();
    const featureFlagsOnly = isDevNoDebug || (this.isProduction && !consentGranted);
    // Assigned BEFORE posthog.init so `isContentCaptureAllowed` (read in the config
    // below) sees the real consent state rather than the default `false`.
    this.isFeatureFlagsOnly = featureFlagsOnly;

    try {
      if (POSTHOG_KEY) {
        posthog.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          // Every content-bearing capture option rides ONE gate. autocapture
          // ships `$el_text` (≤1024 chars) for button/link/input text, which is
          // how an entity row's title reached PostHog on click; heatmaps, dead
          // clicks and exception capture carry page context for the same
          // reason. Fails closed on PHI: an authenticated user whose workspace
          // hasn't resolved yet captures nothing until it proves non-PHI.
          autocapture: this.isContentCaptureAllowed ? this.isProduction : false,
          // For EVERY workspace, PHI or not: autocapture and dead clicks send
          // element tags, classes and position, never an element's text or
          // its attributes. An entity row's `aria-label`/`title` names the
          // note or file, and `href` can be a URL the user typed.
          mask_all_text: true,
          mask_all_element_attributes: true,
          capture_pageview: false,
          capture_pageleave: false,
          capture_exceptions: this.isContentCaptureAllowed,
          persistence: featureFlagsOnly ? 'memory' : 'localStorage+cookie',
          disable_session_recording: !this.isContentCaptureAllowed,
          enable_heatmaps: this.isContentCaptureAllowed,
          // Dead click reduction relies on threshold tuning below.
          // NOTE: `ph-no-capture` class on page elements suppresses general
          // autocapture but NOT dead click events — PostHog's dead click module
          // uses a separate code path that doesn't check this class. That
          // asymmetry is exactly why the PHI decision is made here, not by
          // per-element classes.
          capture_dead_clicks: this.isContentCaptureAllowed
            ? {
                // File pickers and async operations may take longer than the default 2500ms
                mutation_threshold_ms: 3000,
                // Text selection on content pages should not count as dead clicks
                selection_change_threshold_ms: 200,
              }
            : false,
          // Off for every workspace: console output is embedded in the replay
          // payload and can echo user content.
          enable_recording_console_log: false,
          debug: this.isDebug,
          advanced_disable_decide: false, // Need /decide for flags
          // Feature-flags-only mode: drop ALL events as a safety net so nothing
          // from local dev leaks to PostHog. The /decide call for flags still works
          // because it's a separate HTTP request, not an event.
          // In full mode: drop browser extension noise + known test-account
          // traffic before it reaches PostHog.
          before_send: (event) => {
            // Credential redaction runs FIRST, ahead of every drop/allow
            // branch, so no `return` path below can emit an unscrubbed event —
            // including the consent-declined allowlist exception. Magic-link
            // tokens and Firebase oobCodes ride in on the autocaptured page
            // URL ($current_url, $session_entry_url, and nested copies in
            // $set / $set_once / $web_vitals_*_event). See analyticsRedaction.ts.
            const safeEvent = dropLinkTargets(
              dropRetiredSuperProperties(redactEventCredentials(event)),
            );

            // Non-production traffic must not land in the production project.
            // Sits ahead of every branch below (including the consent-decline
            // allowlist) so a preview channel can't emit that one either.
            // before_send is the only hook that sees the two families no other
            // gate can reach: `$pageview` (captured raw by usePageTimeTracking,
            // bypassing track()'s isActive gate) and `$snapshot` (session
            // replay, emitted straight from the recorder). The predicate takes
            // no argument precisely so `$snapshot` — which carries no
            // properties at all — is covered. See isNonProductionAnalyticsEvent.
            if (isNonProductionAnalyticsEvent()) {
              return null;
            }

            // No consent, no events: not even a record of the decline. Flag
            // evaluation is a separate /flags request, not an event.
            if (this.isFeatureFlagsOnly) {
              return null;
            }

            // Defense-in-depth: drop events from known test accounts at
            // the SDK boundary even if they somehow have analytics
            // consent enabled. The server-side helper `captureForUser`
            // already filters lifecycle events server-side; this catches
            // client-side captures (page views, clicks, etc.) that
            // never round-trip through the backend's exclusion list.
            if (isTestAccountEmail(resolveEventEmail(safeEvent))) {
              return null;
            }

            if (safeEvent?.event === '$exception') {
              const exceptionList: Array<{ value?: unknown }> =
                (safeEvent.properties?.['$exception_list'] as Array<{ value?: unknown }>) ?? [];
              const isNoise = exceptionList.some(
                (ex) =>
                  isBrowserExtensionError(ex?.value) ||
                  isCrossOriginScriptError(ex?.value) ||
                  isResizeObserverLoopError(ex?.value),
              );
              if (isNoise) return null;
              // Error messages can echo user content (a note title in a
              // validation error); type and stack are enough to group and fix.
              return stripExceptionMessages(safeEvent);
            }
            return safeEvent;
          },
          // Session-replay ($snapshot) payloads do NOT pass through
          // before_send, so replay needs its own redaction pass — otherwise a
          // recording of the magic-link landing still carries the live token in
          // its captured network/performance entries.
          // Replay masking applies to EVERY workspace (PHI workspaces record
          // nothing at all). Local values win over the project's remote
          // settings for these, so the PostHog UI cannot loosen them; the
          // network body/header switches are the exception (posthog-js ORs
          // local with remote), which is why the mask fn below drops them too.
          // Element attributes (aria-label, title, href) are NOT maskable in
          // rrweb and still record: accepted by David on 2026-10-13.
          session_recording: {
            maskAllInputs: true,
            maskTextSelector: '*',
            blockSelector: 'img, picture, video, audio, canvas, iframe, object, embed',
            captureCanvas: { recordCanvas: false },
            recordHeaders: false,
            recordBody: false,
            // `name` is typed non-optional (CapturedNetworkRequest extends
            // PerformanceEntry) but this callback is invoked by rrweb on
            // arbitrary captured entries — a throw here would break session
            // recording, so guard the type rather than trust it. Mirrors the
            // never-throw contract redactEventCredentials already honours.
            maskCapturedNetworkRequestFn: (request) => ({
              ...request,
              name:
                typeof request.name === 'string'
                  ? stripUrlQuery(redactUrlCredentials(request.name))
                  : request.name,
              requestHeaders: undefined,
              responseHeaders: undefined,
              requestBody: undefined,
              responseBody: undefined,
            }),
          },
        });
        if (!featureFlagsOnly) forgetRetiredFlagProperties(posthog);
        this.applyFlagEnvironment();
        subscribeRegionConsentDefault(this, APP_ENV);

        if (featureFlagsOnly && this.isProduction) {
          console.info('PostHog initialized in feature-flags-only mode (awaiting cookie consent)');
        } else if (featureFlagsOnly) {
          console.info('PostHog initialized in feature-flags-only mode (no event capture)');
        } else if (this.isDebug) {
          // VITE_ANALYTICS_DEBUG is a CONSOLE tool, not an egress path: the
          // host gate in before_send drops every event a non-production bundle
          // produces, so nothing here reaches the PostHog project no matter
          // what this flag says. Verify an event actually lands by shipping it
          // and looking at prod, not by turning this on locally.
          console.info(
            'PostHog Analytics initialized in DEBUG mode — events logged to console only, not sent (non-production host)',
          );
        } else {
          console.info('PostHog Analytics initialized with full tracking (consent granted)');
        }
      } else if (this.isDebug) {
        console.info('Analytics DEBUG mode: no VITE_POSTHOG_KEY set — console logging only');
      } else {
        console.info('Analytics disabled in development mode (no POSTHOG_KEY)');
      }
    } catch (error) {
      console.warn('Failed to initialize PostHog Analytics:', error);
    }

    this.isInitialized = true;
    this.replayQueuedIdentity();
  }

  /**
   * Send the deployment environment with every `/flags` request, so a release
   * condition `environment exact staging` can roll a flag out on staging only.
   * Staging and production share one PostHog project, and this condition is
   * how they are told apart; never a per-environment code gate.
   *
   * A flag-evaluation property only, deliberately NOT an `identify` `$set`:
   * stored on the Person it would flip to whichever environment the user
   * opened last. `reset()` wipes it along with the rest of persistence, so
   * every `reset()` call site re-applies it. No reload: posthog-js debounces
   * every `/flags` request onto a timer and reads these properties when it
   * fires, so the request already queued by `init()` or `reset()` carries it.
   */
  private applyFlagEnvironment(): void {
    posthog.setPersonPropertiesForFlags({ environment: APP_ENV }, false);
  }

  /**
   * Auth state can resolve before requestIdleCallback runs `init()`. Replaying
   * identity here gives cohort-gated flags the authenticated evaluation rather
   * than the anonymous one.
   */
  private replayQueuedIdentity(): void {
    if (this.currentUserId) {
      if (this.currentUserProperties) {
        this.identifyUser(this.currentUserProperties);
      } else if (POSTHOG_KEY) {
        posthog.identify(this.currentUserId);
      }
    }
  }

  /**
   * Upgrade PostHog from feature-flags-only to full analytics after consent.
   * Called when user accepts analytics cookies via the consent banner.
   *
   * @compliance GDPR — only enable tracking after explicit opt-in
   */
  enableAnalyticsAfterConsent(): void {
    if (!this.isFeatureFlagsOnly || !POSTHOG_KEY) return;

    try {
      // Cleared BEFORE set_config so `isContentCaptureAllowed` evaluates the
      // post-consent state. The PHI gate still applies: granting cookie
      // consent inside a PHI workspace must NOT start any content capture.
      this.isFeatureFlagsOnly = false;
      const captureAllowed = this.isContentCaptureAllowed;

      // Re-initialize PostHog with full tracking capabilities
      posthog.set_config({
        autocapture: captureAllowed ? this.isProduction : false,
        persistence: 'localStorage+cookie',
        disable_session_recording: !captureAllowed,
        enable_heatmaps: captureAllowed,
        capture_dead_clicks: captureAllowed,
        capture_exceptions: captureAllowed,
        enable_recording_console_log: false,
      });
      // A reset of stored flag properties drops `environment` too, so it is
      // re-applied before identity is.
      forgetRetiredFlagProperties(posthog);
      this.applyFlagEnvironment();
      // Re-establish identity: a prior revocation called posthog.reset(),
      // so without this a revoke→re-grant cycle leaves every subsequent
      // event on an anonymous distinct_id until the next login.
      this.replayQueuedIdentity();
      console.info('PostHog Analytics upgraded to full tracking after consent');
    } catch (error) {
      console.warn('Failed to enable PostHog analytics after consent:', error);
    }
  }

  /**
   * Push the current gate decision into the live PostHog config.
   *
   * Mirrors every content-bearing option set at `init()` — a mid-session switch
   * into PHI must close autocapture and heatmaps too, not just replay.
   *
   * Belt-and-braces on `stopSessionRecording()`: in the pinned posthog-js
   * (1.364.1) it is an alias for `set_config({disable_session_recording:true})`,
   * and `set_config` itself ends by calling `sessionRecording.
   * startIfEnabledOrStop()` — so the `set_config` above has already stopped the
   * recorder and the explicit call is a duplicate today. It stays because the
   * two paths are separate API surfaces: an SDK change to either one must not
   * be able to silently leave the recorder running.
   */
  private applyContentCaptureGate(): void {
    if (!this.isPostHogReady) return;

    try {
      const captureAllowed = this.isContentCaptureAllowed;
      posthog.set_config({
        autocapture: captureAllowed ? this.isProduction : false,
        disable_session_recording: !captureAllowed,
        enable_heatmaps: captureAllowed,
        capture_dead_clicks: captureAllowed,
        capture_exceptions: captureAllowed,
        enable_recording_console_log: false,
      });
      if (!captureAllowed) {
        posthog.stopSessionRecording();
      }
    } catch (error) {
      console.warn('Failed to apply the PHI content-capture gate:', error);
    }
  }

  /**
   * Publish the active workspace's PHI status to the content-capture gate.
   *
   * Call on every workspace resolution AND every switch. `null` means "no
   * workspace resolved" and re-closes the gate for a signed-in user — see
   * `isContentCaptureAllowed` for why that is not the same as "not PHI".
   *
   * The taint is set BEFORE the unchanged-value early return, so a repeated
   * `true` still marks the session — otherwise a re-entrant call could leave a
   * PHI session untainted and eligible to resume capture after logout.
   */
  setWorkspacePhi(isPhi: boolean | null): void {
    if (isPhi === true) this.phiSessionTainted = true;
    if (this.isPhiWorkspace === isPhi) return;
    this.isPhiWorkspace = isPhi;
    this.applyContentCaptureGate();
  }

  /**
   * Downgrade PostHog to feature-flags-only after consent revocation.
   * Called when user revokes analytics cookies.
   */
  disableAnalyticsAfterRevocation(): void {
    revokeGa4Consent(); // Before the PostHog early return: GA4's cookies outlive it.
    if (this.isFeatureFlagsOnly || !POSTHOG_KEY) return;

    try {
      posthog.set_config({
        autocapture: false,
        persistence: 'memory',
        disable_session_recording: true,
        enable_heatmaps: false,
        capture_dead_clicks: false,
        capture_exceptions: false,
        enable_recording_console_log: false,
      });
      // set_config alone doesn't stop an ALREADY-RUNNING recorder — rrweb
      // keeps posting $snapshot payloads (which bypass before_send) until
      // reload. Stop it explicitly so revocation is immediate.
      posthog.stopSessionRecording();

      posthog.reset();
      this.isFeatureFlagsOnly = true;
      this.applyFlagEnvironment();
      // Restore flag-targeting identity — reset() would otherwise leave
      // flags evaluating for a fresh anonymous id until the next reload,
      // flipping cohort-gated UI mid-session. This lands us in exactly the
      // PRE-consent state, which also identifies for flag targeting: no
      // cookie (memory persistence) and before_send drops every event, so
      // nothing is captured — only the gate decisions stay correct.
      this.replayQueuedIdentity();
      console.info('PostHog Analytics downgraded to feature-flags-only after consent revocation');
    } catch (error) {
      console.warn('Failed to disable PostHog analytics after revocation:', error);
    }
  }

  /**
   * Set user ID with error handling
   */
  setUserId(userId: string): void {
    const wasSignedOut = this.currentUserId === null;
    this.currentUserId = userId;
    // Signing in closes the gate until the workspace proves non-PHI. Without
    // this, a signed-out session (replay allowed — no workspace content on the
    // marketing/auth surfaces) keeps recording straight through login into a
    // PHI workspace's dashboard.
    if (wasSignedOut && this.isPhiWorkspace === null) {
      this.applyContentCaptureGate();
    }
    if (!this.isPostHogReady && !this.isActive) return;

    try {
      if (this.isPostHogReady) {
        posthog.identify(userId);
      }
    } catch (error) {
      console.warn('Failed to set PostHog user ID:', error);
    }
  }

  /**
   * Identify user with properties.
   * Works in feature-flags-only mode so flag targeting by email/properties works locally.
   */
  identifyUser(userProperties: Partial<UserProperties>): void {
    this.currentUserProperties = userProperties;
    if (!this.isPostHogReady && !this.isActive) return;

    try {
      if (this.isPostHogReady && this.currentUserId) {
        const email = userProperties.email || '';
        const emailDomain = email.split('@')[1] || '';
        // `is_internal: true` for any @bowerlabs.ai email — keeps the
        // canonical PostHog cohort definition matchable on every login,
        // not just at signup. Server-side captures the same flag at
        // signup (see `auth.controller.ts`); replicating it here means
        // a Person record stays correctly tagged even if the signup
        // event was ever dropped (PostHog init order, etc).
        const isInternal = emailDomain === 'bowerlabs.ai';

        posthog.identify(
          this.currentUserId,
          {
            ...userProperties,
            email_domain: emailDomain,
            is_internal: isInternal,
          },
          {
            // Seed `is_blocked: false` as a $set_once default so every real
            // user record carries the property and it's a one-click toggle to
            // `true` (which adds them to the "Blocked / disabled accounts"
            // cohort). MUST stay in the $set_once channel — identify runs on
            // every login, and a $set here would reset a manually-blocked
            // user back to false. $set_once only writes when the property is
            // absent, so an existing `true` is never clobbered.
            is_blocked: false,
          },
        );

        // Reload feature flags after identify so cohort-based flags
        // re-evaluate with the now-known person properties.
        posthog.reloadFeatureFlags();
      }
    } catch (error) {
      console.warn('Failed to identify user in PostHog:', error);
    }
  }

  /**
   * Track event with error handling.
   * Automatically enriches with `platform` (can be overridden by caller).
   *
   * **Generic signature:** the type parameter `T extends BaseEventProperties`
   * lets call sites pass either a typed event shape (one of the
   * `*Properties` interfaces in `types/analytics.ts`, for full IDE completion) OR a
   * loose `{key: value, ...}` object that only conforms to
   * `BaseEventProperties` (arbitrary keys allowed via the index signature).
   * Either way, no `as any` / `as never` cast is required at the call site.
   * The runtime behaviour is identical: PostHog gets the merged object.
   */
  track<T extends BaseEventProperties = BaseEventProperties>(
    eventName: AnalyticsEventName,
    eventProperties?: T,
  ): void {
    if (!this.isActive) return;

    const workspaceId = eventProperties?.workspace_id;
    const enriched = {
      platform: getPlatform(),
      environment: APP_ENV,
      ...eventProperties,
      ...(typeof workspaceId === 'string' && workspaceId.length > 0
        ? { $groups: { workspace: workspaceId } }
        : {}),
    };

    if (this.isDebug) {
      console.info(`[Analytics] ${eventName}`, enriched);
    }

    try {
      if (POSTHOG_KEY) {
        posthog.capture(eventName, enriched as Record<string, unknown>);
      }
    } catch (error) {
      console.warn('Failed to capture PostHog event:', error);
    }
  }

  /**
   * Track event immediately — alias for track().
   * Kept for callsite compatibility.
   */
  trackImmediate<T extends BaseEventProperties = BaseEventProperties>(
    eventName: AnalyticsEventName,
    eventProperties?: T,
  ): void {
    this.track(eventName, eventProperties);
  }

  /**
   * Capture an exception to PostHog error tracking.
   * Use in catch blocks and error boundaries for structured error reporting.
   *
   * Silent whenever content capture is shut: a PHI workspace, and also the
   * window in which a signed-in user's workspace has not resolved yet (the
   * gate fails closed, and every auth transition passes through it). Callers
   * that need the failure recorded in those cases must ALSO write a Cloud
   * Logging line — `ErrorBoundary` does; `useLiveMode`, `useStandbyMode` and
   * `ShareEntityDialog` currently do not, so their errors go unrecorded there.
   * Messages are replaced in `before_send` for every workspace
   * (`stripExceptionMessages`), so lifting this silence would not egress the
   * message; it is kept because the stack and component trace still describe
   * the page.
   */
  captureException(error: Error, additionalProperties?: Record<string, unknown>): void {
    if (!this.isActive) return;
    // Rides the content gate with autocapture and replay, because it carries
    // the same class of payload. `capture_exceptions: false` only unhooks
    // posthog-js's AUTOMATIC handlers; this explicit call is an ordinary
    // capture, so it reached PostHog from a PHI workspace regardless. A
    // `$exception` ships the error message and stack in `$exception_list`, and
    // an error message is the classic UGC carrier — extractors put document
    // content in it (#630) and providers quote content back. POSTHOG is
    // `baa_covered: false` in config/egress-policy.yaml, so a PHI workspace
    // loses client exception tracking rather than the boundary sending it.
    if (!this.isContentCaptureAllowed) return;

    try {
      if (POSTHOG_KEY) {
        posthog.captureException(error, additionalProperties);
      }
    } catch (captureError) {
      console.warn('Failed to capture PostHog exception:', captureError);
    }
  }

  /**
   * Update a person property. Overwrites any existing value.
   * Used to set feature-usage timestamps (e.g. last_bird_chat_at) so PostHog
   * survey targeting can exclude users who recently used a feature.
   * Pass `null` to UNSET (remove) a previously-set property — sent as a
   * `$unset`, because `setPersonProperties({key: null})` would transmit
   * `$set: {key: null}` and leave the property present with a JSON null.
   */
  setPersonProperty(key: string, value: string | null): void {
    if (!this.isActive) return;

    try {
      if (POSTHOG_KEY) {
        if (value === null) {
          // posthog-js 1.364 has no unsetPersonProperties() helper; per
          // PostHog docs, removal is an event carrying a `$unset` array of
          // property keys — `$set` is the canonical person-properties event.
          posthog.capture('$set', { $unset: [key] });
        } else {
          posthog.setPersonProperties({ [key]: value });
        }
      }
    } catch (error) {
      console.warn('Failed to set PostHog person property:', error);
    }
  }

  /**
   * Set user property once (won't overwrite existing value)
   */
  setUserPropertyOnce(propertyName: string, value: unknown): void {
    if (!this.isActive) return;

    try {
      if (POSTHOG_KEY && this.currentUserId) {
        posthog.identify(this.currentUserId, undefined, { [propertyName]: value });
      }
    } catch (error) {
      console.warn('Failed to set PostHog user property once:', error);
    }
  }

  /**
   * Create a PostHog alias linking two distinct IDs to the same person.
   * Used once per user to merge the historical Firebase UID identity with the
   * new internal Prisma UUID identity. Idempotent — safe to call on every login.
   */
  aliasUser(newId: string, oldId: string): void {
    if (!this.isPostHogReady) return;
    try {
      posthog.alias(newId, oldId);
    } catch (error) {
      console.warn('Failed to alias PostHog user:', error);
    }
  }

  /**
   * Register super properties that are automatically included in every event.
   * Call when user/workspace context changes (e.g. workspace switch, login).
   * These persist in localStorage until cleared or overwritten.
   */
  registerContext(context: Record<string, unknown>): void {
    if (!this.isActive) return;

    try {
      if (POSTHOG_KEY) {
        posthog.register(context);
      }
    } catch (error) {
      console.warn('Failed to register PostHog context:', error);
    }
  }

  /**
   * Associate events with a workspace group
   */
  setWorkspaceGroup(workspaceId: string, properties?: Record<string, unknown>): void {
    if (!this.isActive) return;

    try {
      if (POSTHOG_KEY) {
        posthog.group('workspace', workspaceId, properties);
      }
    } catch (error) {
      console.warn('Failed to set PostHog workspace group:', error);
    }
  }

  /**
   * Register a callback to run before logout (while user is still identified).
   * Used by tracking hooks to flush final events before posthog.reset().
   */
  registerPreLogoutCallback(callback: () => void): void {
    this.preLogoutCallbacks.add(callback);
  }

  unregisterPreLogoutCallback(callback: () => void): void {
    this.preLogoutCallbacks.delete(callback);
  }

  /**
   * Clear user data on logout.
   * Runs pre-logout callbacks first so final events (page time, session end)
   * are sent while the user is still identified.
   */
  clearUser(): void {
    // Auth can resolve before deferred PostHog initialization. Clear both
    // identity caches even while analytics is inactive so init cannot replay a
    // logged-out user's identify call later.
    this.currentUserId = null;
    this.currentUserProperties = null;
    if (!this.isActive) return;

    // Run pre-logout callbacks to flush final events while user is still identified
    for (const callback of this.preLogoutCallbacks) {
      try {
        callback();
      } catch (error) {
        console.warn('Pre-logout callback error:', error);
      }
    }

    try {
      if (this.isPostHogReady) {
        posthog.reset();
        this.applyFlagEnvironment();
      }
    } catch (error) {
      console.warn('Failed to clear PostHog user:', error);
    }
  }

  /**
   * Get initialization status
   */
  isReady(): boolean {
    return this.isActive;
  }
}

// Export singleton instance
export const safeAnalytics = new SafeAnalytics();

/**
 * Hard-coded list of stable team / bot accounts whose traffic must
 * never reach PostHog regardless of what the backend exclusion list
 * has — frontend-only belt-and-suspenders.
 *
 * Ephemeral E2E users (suffix `@bower-e2e.test`) are matched
 * separately in `isTestAccountEmail`. Anything matching the suffix
 * already round-trips through `isE2ETestUser` server-side; this
 * filter catches client-side captures (page views, clicks, etc.)
 * that don't.
 */
const STATIC_TEST_ACCOUNT_EMAILS = new Set<string>(['test-bot@bowerlabs.ai']);

const E2E_EMAIL_SUFFIX = '@bower-e2e.test';

/**
 * Returns true if `email` looks like a test / bot account whose events
 * must be excluded from production analytics. Case-insensitive.
 *
 * Exported via `safeAnalytics`'s before_send filter — kept here as a
 * pure helper so it can be unit-tested without booting the SDK.
 */
export function isTestAccountEmail(email: string | null | undefined): boolean {
  if (typeof email !== 'string' || email.length === 0) return false;
  const lower = email.toLowerCase();
  if (lower.endsWith(E2E_EMAIL_SUFFIX)) return true;
  if (STATIC_TEST_ACCOUNT_EMAILS.has(lower)) return true;
  return false;
}

/**
 * Returns true when this bundle is NOT the production app, so `before_send`
 * can drop the event.
 *
 * Two independent discriminators, because neither alone is sufficient:
 *
 *  1. `APP_ENV` — catches the dev server (`vite` sets `import.meta.env.PROD`
 *     false → `APP_ENV: 'development'`), which is how `localhost:3000` events
 *     reach production whenever a developer sets `VITE_ANALYTICS_DEBUG=true`.
 *  2. The host — the ONLY thing that catches Firebase preview channels, whose
 *     bundles resolve `APP_ENV` to `'production'` exactly like prod does. It
 *     also catches `benenota-e6b50.web.app`, the default hosting domain, which
 *     `canonicalHostRedirect.ts` now sends to `app.bowerlabs.ai` before boot.
 *
 * **Deliberately takes no argument.** The gate asks a question about the
 * DOCUMENT, not about the payload, and reads `window.location` via the shared
 * {@link isProductionHost} — the same source of truth `ga4.ts` uses. An earlier
 * revision read `properties.$host` off the event and failed OPEN when it was
 * missing; posthog-js short-circuits property stamping for `$snapshot`, so
 * session replay (the highest-content capture family there is) never carries
 * `$host` and was let through from every preview channel. Reading the location
 * covers every event family, present and future, and deletes the fail-open
 * tradeoff rather than defending it.
 */
export function isNonProductionAnalyticsEvent(): boolean {
  if (APP_ENV !== 'production') return true;
  return !isProductionHost();
}

/**
 * Resolve the user email associated with an outgoing PostHog event.
 *
 * Looks at, in order:
 *   1. `properties.$set.email` — populated on identify-style captures
 *      and on lifecycle events that piggy-back $set on the same
 *      payload (e.g. our server-side User Signed Up).
 *   2. `properties.email` — explicit caller-provided email.
 *   3. `posthog.get_property('email')` — the locally-stored person
 *      property set by a prior `identify(...)` call.
 *
 * Returns null when none of the above resolve. Pure function — never
 * throws (the get_property call is wrapped) so it's safe inside
 * before_send.
 */
export function resolveEventEmail(event: unknown): string | null {
  const props = (event as { properties?: Record<string, unknown> } | null | undefined)?.properties;
  if (!props) return null;

  const setBlock = props['$set'] as Record<string, unknown> | undefined;
  if (setBlock && typeof setBlock.email === 'string') return setBlock.email;

  if (typeof props.email === 'string') return props.email;

  try {
    const stored = posthog.get_property('email');
    if (typeof stored === 'string') return stored;
  } catch {
    // posthog SDK may not be fully initialised; fall through.
  }

  return null;
}

/**
 * Detect the current platform based on device characteristics.
 * Returns 'mobile_web' for mobile/touch devices, 'web' for desktop.
 */
export function getPlatform(): 'web' | 'mobile_web' {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobileUA = /iphone|ipad|ipod|android|mobile/.test(userAgent);
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.matchMedia('(max-width: 48em)').matches;

  return isMobileUA || (isTouchDevice && isSmallScreen) ? 'mobile_web' : 'web';
}

/**
 * Get page category from pathname
 */
export function getPageCategory(pathname: string): PageCategory {
  // Mobile-specific routes (check before generic patterns)
  if (pathname.includes('/m/capture')) return PageCategory.MOBILE_CAPTURE;
  if (pathname.includes('/m/live')) return PageCategory.LIVE;
  if (pathname.includes('/m/notes')) return PageCategory.NOTES;

  // Main app routes
  if (pathname.includes('/collections') || pathname.includes('/projects'))
    return PageCategory.COLLECTIONS;
  if (pathname.includes('/artifacts')) return PageCategory.ARTIFACTS;
  if (pathname.includes('/notes')) return PageCategory.NOTES;
  if (pathname === '/' || pathname.includes('/dashboard')) return PageCategory.DASHBOARD;
  if (pathname.includes('/settings') || pathname.includes('/account')) return PageCategory.SETTINGS;
  if (pathname.includes('/chat')) return PageCategory.CHAT;
  if (pathname.includes('/onboarding')) return PageCategory.ONBOARDING;
  if (pathname.includes('/audit')) return PageCategory.AUDIT;

  // Auth-related routes
  if (
    pathname.includes('/auth') ||
    pathname.includes('/login') ||
    pathname.includes('/verify') ||
    pathname.includes('/reset-password') ||
    pathname.includes('/authorize') ||
    pathname.includes('/oauth')
  )
    return PageCategory.AUTH;

  return PageCategory.OTHER;
}

/**
 * Determine if a page is a list view or detail view based on pathname.
 * Detail pages have an ID segment after the base path.
 */
export function getPageViewType(pathname: string): PageViewType | undefined {
  // Detail pages: /collections/:id, /notes/:id, /m/notes/:id
  if (/\/collections\/[^/]+/.test(pathname)) return 'detail';
  if (/\/notes\/[^/]+/.test(pathname)) return 'detail';

  // List pages: /collections, /artifacts, /notes, /m/notes
  if (/\/collections\/?$/.test(pathname)) return 'list';
  if (/\/artifacts\/?$/.test(pathname)) return 'list';
  if (/\/(?:m\/)?notes\/?$/.test(pathname)) return 'list';

  return undefined;
}

/**
 * Which copy affordance failed. One value per hand-rolled `navigator.clipboard`
 * call site; keep it in step with those sites so the dashboard can name them.
 */
export type ClipboardSurface =
  | 'equipment_qr_link'
  | 'bird_table'
  | 'mcp_server_url'
  | 'audit_event_id';

/**
 * Report a copy that did not happen.
 *
 * Shared by every hand-rolled copy affordance so the two property values can't
 * drift apart across sites — `reason` in particular separates "this browser has
 * no clipboard API" (an insecure context, which is a deployment/access problem)
 * from "the write was refused" (a permission problem). Without this the fix for
 * the silent-copy class is itself invisible: the user now sees an error and we
 * would still have no idea how often it fires.
 */
export function trackClipboardCopyFailed(
  surface: ClipboardSurface,
  reason: 'unavailable' | 'rejected',
): void {
  safeAnalytics.track(ANALYTICS_EVENTS.CLIPBOARD_COPY_FAILED, { surface, reason });
}
